import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import {
  SPIDER_CHART_CONSTANTS,
  generateSpiderChartPolygonPoints,
  generateSpiderChartBackgroundPolygons,
  calculateSpiderChartLabelPosition,
  generateSpiderChartLevel1Polygon,
  calculateSpiderChartAxisLine,
} from '../../utils/spiderChartUtils';
import { SuspiciousActivityBadge } from '../ui/SuspiciousActivityBadge';
import { Info, AlertTriangle } from 'lucide-react';

interface SkillScore {
  name: string;
  value: number; // Can be 1-5 or 1-10 scale
}

interface SkillTooltipData {
  title: string;
  level: number | undefined;
  explanation: string;
  hasSuspiciousActivity?: boolean;
}

interface SpiderChartProps {
  skills: SkillScore[];
  tooltips?: { [skillName: string]: SkillTooltipData[] };
  scale?: 5 | 10; // Scale for the chart (5 for soft skills, 10 for technical skills)
}

export const SpiderChart: React.FC<SpiderChartProps> = ({ skills, tooltips, scale = 5 }) => {
  const { t } = useTranslation('candidates');
  const [visibleTooltip, setVisibleTooltip] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const constants = SPIDER_CHART_CONSTANTS;

  const handleTooltipShow = (skillName: string, event: React.MouseEvent) => {
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    setTooltipPosition({
      x: rect.right + 8,
      y: rect.top + rect.height / 2
    });
    setVisibleTooltip(skillName);
  };

  const handleTooltipHide = () => {
    setVisibleTooltip(null);
  };

  const renderTooltip = (skillName: string) => {
    const skillTooltips = tooltips?.[skillName];
    if (!skillTooltips || skillTooltips.length === 0 || visibleTooltip !== skillName) {
      return null;
    }

    const hasSuspicion = skillTooltips.some(t => t.hasSuspiciousActivity);
    const widthClass = hasSuspicion ? "w-100" : "w-80";

    const tooltipContent = (
      <div
        className={`fixed ${widthClass} bg-gray-900 text-white text-sm rounded-lg p-3 shadow-xl z-[9999] pointer-events-none`}
        style={{
          left: `${tooltipPosition.x}px`,
          top: `${tooltipPosition.y}px`,
          transform: 'translateY(-50%)',
        }}
      >
        <div className="space-y-2">
          {skillTooltips.map((item, index) => (
            <div key={index}>
              <div className="font-medium text-purple flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span>{item.title}</span>
                  {item.hasSuspiciousActivity && (
                    <SuspiciousActivityBadge />
                  )}
                </div>
                {item.level !== undefined && (
                  <span className="text-xs bg-purple text-dark-invirtus px-2 py-1 rounded">
                    {item.level}/{scale}
                  </span>
                )}
              </div>
              {item.hasSuspiciousActivity && (
                 <div className="text-red-400 text-xs mt-1 font-semibold mb-1">
                   {t('meta_cv.spider_chart.suspicious_behaviour')}
                 </div>
              )}
              <div className="text-gray-300 text-xs mt-1">{item.explanation}</div>
            </div>
          ))}
        </div>
        <div
          className="absolute w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-900"
          style={{
            right: '100%',
            top: '50%',
            transform: 'translateY(-50%)'
          }}
        ></div>
      </div>
    );

    return createPortal(tooltipContent, document.body);
  };

  return (
    <div className="flex justify-center">
      <div className="relative w-full h-64 px-10 pb-4" style={{ overflow: 'visible' }}>
        <svg viewBox="0 0 250 250" className="w-full h-full" style={{ overflow: 'visible' }}>
          
          {/* Background pentagons with level labels */}
          {generateSpiderChartBackgroundPolygons(skills.length, scale, constants).map((polygon, index) => (
            <g key={index}>
              <polygon 
                points={polygon.points}
                fill="none" 
                stroke="#E1D2DA"
                strokeWidth="1"
              />
              {/* Level labels at the top of each background polygon */}
              <text 
                x={constants.CHART_CENTER_X} 
                y={constants.CHART_CENTER_Y - (constants.OUTER_RADIUS * (polygon.level - 1) / (scale - 1))} 
                textAnchor="middle" 
                alignmentBaseline="middle"
                fontSize="8" 
                fill="#9ca3af"
                className="font-medium"
              >
                {polygon.level}
              </text>
            </g>
          ))}
          
          {/* Level 1 polygon (minimum kernel) */}
          <polygon 
            points={generateSpiderChartLevel1Polygon(skills.length, constants)}
            fill="none"
            stroke="#E1D2DA"
            strokeWidth="1"
          />
          <text
            x={constants.CHART_CENTER_X} 
            y={constants.CHART_CENTER_Y - constants.MIN_RADIUS} 
            textAnchor="middle" 
            alignmentBaseline="middle"
            fontSize="8" 
            fill="#9ca3af"
            className="font-medium"
          >
            1
          </text>
          
          {/* Axis lines */}
          {skills.map((_, index) => {
            const axisLine = calculateSpiderChartAxisLine(index, skills.length, constants);
            return (
              <line 
                key={index}
                x1={axisLine.x1} 
                y1={axisLine.y1} 
                x2={axisLine.x2} 
                y2={axisLine.y2} 
                stroke="#E1D2DA"
                strokeWidth="1"
              />
            );
          })}
          
          {/* Data polygon */}
          <polygon 
            points={generateSpiderChartPolygonPoints(skills, scale, constants)}
            fill="rgba(217, 173, 255, 0.45)"
            stroke="#9F7AD0"
            strokeWidth="2"
          />
          
          {/* Axis labels */}
          {skills.map((skill, index) => {
            const labelPos = calculateSpiderChartLabelPosition(index, skills.length, true, constants);
            const hasTooltip = tooltips?.[skill.name] && tooltips[skill.name].length > 0;
            const hasSuspiciousActivity = tooltips?.[skill.name]?.some(t => t.hasSuspiciousActivity) ?? false;

            // Split long names into two lines (2+ words if total length > 14, or 3+ words always)
            const words = skill.name.split(' ');
            const isMultiLine = words.length >= 3 || (words.length >= 2 && skill.name.length > 14);
            const labelLines = isMultiLine
              ? [words.slice(0, Math.ceil(words.length / 2)).join(' '), words.slice(Math.ceil(words.length / 2)).join(' ')]
              : [skill.name];
            const longestLine = labelLines.reduce((a, b) => a.length > b.length ? a : b, '');

            // Calculate foreignObject position and dimensions — cap width to prevent overflow
            const maxLabelWidth = 100;
            const estimatedTextWidth = Math.min(longestLine.length * 6 + (hasTooltip ? 16 : 0), maxLabelWidth);
            const labelHeight = isMultiLine ? 28 : 16;

            // Determine x position and justify based on text anchor
            let foX: number;
            let justifyContent: string;

            if (labelPos.textAnchor === 'start') {
              // Right side labels - align left
              foX = labelPos.x + (labelPos.dx || 0);
              justifyContent = 'flex-start';
            } else if (labelPos.textAnchor === 'end') {
              // Left side labels - align right
              foX = labelPos.x + (labelPos.dx || 0) - estimatedTextWidth;
              justifyContent = 'flex-end';
            } else {
              // Center labels (top/bottom)
              foX = labelPos.x - estimatedTextWidth / 2;
              justifyContent = 'center';
            }

            const foY = labelPos.y + (labelPos.dy || 0) - labelHeight / 2;

            return (
              <foreignObject
                key={index}
                x={foX}
                y={foY}
                width={estimatedTextWidth + 10}
                height={labelHeight}
                style={{ overflow: 'visible' }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent,
                    gap: '3px',
                    height: '100%',
                    fontSize: '10px',
                    fontWeight: 500,
                    color: hasSuspiciousActivity ? '#EF4444' : '#4b5563',
                    cursor: hasTooltip ? 'help' : 'default',
                  }}
                  onMouseEnter={hasTooltip ? (e) => handleTooltipShow(skill.name, e) : undefined}
                  onMouseLeave={hasTooltip ? handleTooltipHide : undefined}
                >
                  {hasTooltip && (
                    hasSuspiciousActivity ? (
                      <AlertTriangle size={10} strokeWidth={2} color="#EF4444" />
                    ) : (
                      <Info size={10} strokeWidth={2} color="#6B7280" />
                    )
                  )}
                  <span style={{
                    textAlign: labelPos.textAnchor === 'end' ? 'right' : labelPos.textAnchor === 'start' ? 'left' : 'center',
                    lineHeight: '13px',
                    maxWidth: `${maxLabelWidth - (hasTooltip ? 16 : 0)}px`,
                    display: 'inline-block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {labelLines.map((line, i) => (
                      <span key={i}>{line}{i < labelLines.length - 1 && <br />}</span>
                    ))}
                  </span>
                </div>
              </foreignObject>
            );
          })}
        </svg>
        
        {/* Render tooltips */}
        {skills.map(skill => renderTooltip(skill.name))}
      </div>
    </div>
  );
}; 